import React from 'react';
import TickCircle from '../../icons/tick-circle.svg';
import TDR, { LANDING_PAGE_PLACEHOLDERS } from 'tdr-common';
import { useTranslation } from 'react-i18next';
import {
	analytics,
	LINK_CLICKED_EVENT,
	REDIRECT_TO_RMS_FROM_ASSIGNED_SEATING_CARD
} from '../../helpers/analytics';
import { RESULT_CLICKED } from '../../helpers/analytics/constants';
import { setLang } from '../../helpers/setLang';
import { getPlatformDetails, getDisplayPhoneNumber, getDisplayEmail } from '../../helpers/alternativePlatformVendor';
import { resolveLocalizedText } from '../../helpers/resolveLocalizedText';

type AssignedSeatingCardProps = {
	restaurant: TDR.TDR.Restaurant;
	header?: TDR.TDR.SearchConfig.LocalizedCopy;
	body?: TDR.TDR.SearchConfig.LocalizedCopy;
};

export const AssignedSeatingCard = ({ restaurant, header, body }: AssignedSeatingCardProps) => {
	const { t, i18n } = useTranslation();

	const alternativePlatform = restaurant.settings?.alternativePlatform;
	if (!alternativePlatform) {
		return null;
	}

	let href = alternativePlatform.href;
	if (i18n.language !== 'en') {
		href = setLang(href, i18n.language);
	}

	const handleClick = () => {
		analytics.track(RESULT_CLICKED, { resultType: 'standard seating' });
		analytics.track(LINK_CLICKED_EVENT, {
			href,
			purpose: REDIRECT_TO_RMS_FROM_ASSIGNED_SEATING_CARD,
			platform: alternativePlatform.vendor
		});
	};

	const { displayName } = getPlatformDetails(alternativePlatform.vendor);
	const isCustom = alternativePlatform.vendor === 'custom';
	const isPhone = alternativePlatform.vendor === 'phone';
	const isEmail = alternativePlatform.vendor === 'email';
	const isRmsPlatform = !isCustom && !isPhone && !isEmail;
	const platformLogoSrc = `../../images/poweredBy/${alternativePlatform.vendor}.svg`;

	// The card has no default title: the "Standard Seating" section heading sits
	// directly above it, so a default here just repeats itself. A title renders
	// only where an operator configured one.
	const getTitle = () => {
		if (header) {
			return resolveLocalizedText(header, i18n.language);
		}
		if (isCustom) {
			return resolveLocalizedText(restaurant.settings?.landingPage?.alternate?.title, i18n.language);
		}
		return null;
	};

	const getDescription = () => {
		if (body) {
			return resolveLocalizedText(body, i18n.language);
		}
		if (isCustom) {
			return resolveLocalizedText(restaurant.settings?.landingPage?.alternate?.callToAction, i18n.language) || LANDING_PAGE_PLACEHOLDERS.alternate.callToAction;
		}
		if (isPhone) {
			return `${t('inventory.book_regular_by_calling_prefix')} ${getDisplayPhoneNumber(alternativePlatform.href)}`;
		}
		if (isEmail) {
			return `${t('inventory.book_regular_by_emailing_prefix')} ${getDisplayEmail(alternativePlatform.href)}`;
		}
		return t('inventory.assigned_seating_description', { platformName: displayName });
	};

	const title = getTitle();

	return (
		<a
			href={href}
			target='_blank'
			rel='noopener noreferrer'
			onClick={handleClick}
			data-cy='assigned-seating-card'
			className='flex w-full flex-col items-start overflow-hidden rounded-2xl border border-white/10 bg-tablz-med-gray-3 p-1 transition-colors hover:bg-tablz-med-gray-6'
		>
			<div className='flex w-full flex-col items-start justify-center px-4 py-6'>
				<div className='flex w-full flex-col gap-6'>
					<div className='flex flex-col gap-2'>
						{title && (
							<p className='font-lora text-[20px] font-medium leading-snug text-white truncate'>
								{title}
							</p>
						)}
						<p className='font-poppins text-sm leading-6 text-white/70'>
							{getDescription()}
						</p>
					</div>
					{isRmsPlatform && (
						<div className='flex items-center gap-2 py-1.5'>
							<span className='font-poppins text-xs font-medium uppercase tracking-wide text-white/50'>
								{t('powered_by')}
							</span>
							<img src={platformLogoSrc} alt={displayName} className='h-5 w-auto max-w-[130px]' style={{ filter: 'brightness(0) invert(1)' }} />
						</div>
					)}
				</div>
			</div>
			<div className='flex w-full items-center justify-between rounded-2xl bg-white/[0.04] p-3'>
				<div className='flex items-center gap-1'>
					<img src={TickCircle} alt='' width={16} height={16} aria-hidden />
					<span className='font-poppins text-sm leading-none text-green-400'>
						{t('inventory.available')}
					</span>
				</div>
				<span className='font-poppins text-sm font-semibold leading-none text-zinc-200'>
					{t('inventory.assigned_seating_proceed')}
				</span>
			</div>
		</a>
	);
};
